import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Motion";
import CtaButton from "@/components/CtaButton";
import BookReveal from "@/components/BookReveal";
import { books, teaser, firstBook, getBookStage, images, aboutBugg, arcOpenAt } from "@/config/site";

const SYNOPSIS = [
  "Katia Karter has spent ten years living with the story she was given about her father\u2019s murder.",
  "Kendell \u201cK-T\u201d Karter was her Poppa\u2014loving, protective, and larger than life. But as Katia turns eighteen, the past begins reaching for her in ways she cannot explain. A stranger calls her \u201cPrincess.\u201d Photographs surface that should not exist. And a message suggests that the truth about her father\u2019s death was never as simple as her family made it seem.",
  "The more Katia questions what happened, the more the people closest to her urge her to leave it alone.",
  "But silence has its own answers.",
  "Caught between family loyalty and a growing need to know who her father really was, Katia begins uncovering the hidden history behind the Karter name\u2014one shaped by secrets, danger, old alliances, and promises that were never meant to reach her.",
  "Now she has to decide who she can trust before the truth costs more than she is ready to lose.",
];

function PresaleCountdown() {
  const presaleAt = new Date(firstBook.presaleOpenAt).getTime();
  const releaseAt = new Date(firstBook.releaseOpenAt).getTime();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Phase-aware: presale countdown -> release countdown (Dec 1) -> live (Jan 19).
  const phase = now < presaleAt ? "presale" : now < releaseAt ? "release" : "live";
  const target = phase === "presale" ? presaleAt : releaseAt;
  const remaining = target - now;

  return (
    <section data-testid="book-presale" className="py-20 sm:py-24">
      <div className="mx-auto max-w-[780px] px-5 text-center sm:px-8">
        <Reveal>
          <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">
            {phase === "presale" ? "Presale Begins In" : phase === "release" ? "The Lost Girl Arrives In" : "Available Now"}
          </p>
          <div className="gold-rule mx-auto mt-6 w-16" />
        </Reveal>
        {phase !== "live" ? (
          <Reveal delay={0.1}>
            <div data-testid="book-presale-countdown" className="mt-10 flex items-start justify-center gap-6 sm:gap-12">
              {[
                ["Days", Math.floor(remaining / 86400000)],
                ["Hours", Math.floor(remaining / 3600000) % 24],
                ["Minutes", Math.floor(remaining / 60000) % 60],
                ["Seconds", Math.floor(remaining / 1000) % 60],
              ].map(([label, value]) => (
                <div key={label} data-testid={`countdown-${label.toLowerCase()}`} className="min-w-[3.5rem]">
                  <p className="font-display text-4xl tabular-nums text-dh-cream sm:text-6xl">{String(value).padStart(2, "0")}</p>
                  <p className="mt-2 font-body text-[10px] uppercase tracking-[0.22em] text-dh-muted">{label}</p>
                </div>
              ))}
            </div>
            {phase === "release" && (
              <p data-testid="book-presale-open" className="mt-8 font-body text-sm text-dh-muted">
                Presale is now open.
              </p>
            )}
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <p data-testid="book-release-live" className="mt-10 font-serif-soft text-2xl italic text-dh-cream sm:text-3xl">
              {firstBook.title} is available now.
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export default function Books() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "20%"]);
  const stage = getBookStage();
  const titleRevealed = stage >= 2 && firstBook.title;
  const heroHeadline = titleRevealed ? [firstBook.title] : ["Something", "Is Coming."];

  return (
    <>
      <section ref={ref} data-testid="books-hero" className="relative flex min-h-[50svh] items-center overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 -z-10">
          <img src={images.keyMotif} alt="A single antique key resting in shadow" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-dh-bg/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-dh-bg via-dh-bg/60 to-dh-bg/70" />
        </motion.div>

        <div className="mx-auto w-full max-w-[1100px] px-5 sm:px-8 pt-20 pb-10">
          <Reveal>
            <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">{titleRevealed ? "The Debut Novel" : "The First Story"}</p>
          </Reveal>
          <h1 className="mt-6 font-display text-4xl leading-[1.05] text-dh-cream sm:text-6xl lg:text-7xl">
            <MaskLines lines={heroHeadline} />
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl font-serif-soft text-xl italic text-dh-cream/80 sm:text-2xl">
              {titleRevealed && firstBook.seriesName ? firstBook.seriesName : books.supporting}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Staged reveal (title / cover / preorder / retailers) once stage advances */}
      <BookReveal />

      {titleRevealed && <PresaleCountdown />}

      {titleRevealed && (
        <>
          {/* About the Story — space reserved for the official synopsis */}
          <section data-testid="book-about-story" className="border-t border-dh-cream/5 bg-dh-bg2 py-24 sm:py-32">
            <div className="mx-auto max-w-[780px] px-5 sm:px-8">
              <Reveal>
                <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">About the Story</p>
                <div className="gold-rule mt-6 w-16" />
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-10 space-y-6" data-testid="book-synopsis">
                  {SYNOPSIS.map((p, i) => (
                    <p key={i} className="font-body text-base leading-[1.9] text-dh-cream/80 sm:text-lg sm:leading-[1.9]">
                      {p}
                    </p>
                  ))}
                </div>
              </Reveal>
            </div>
          </section>

          {/* Themes / Reader Positioning */}
          <section data-testid="book-themes" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[900px] px-5 sm:px-8 text-center">
              <Reveal>
                <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Themes & Reader Positioning</p>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mx-auto mt-10 max-w-3xl font-display text-2xl leading-[1.9] text-dh-cream/85 sm:text-3xl sm:leading-[1.9]">
                  Urban Fiction <span className="text-dh-gold/60">&middot;</span> Black Fiction <span className="text-dh-gold/60">&middot;</span> Crime &amp; Suspense <span className="text-dh-gold/60">&middot;</span> Mystery <span className="text-dh-gold/60">&middot;</span> Family Drama <span className="text-dh-gold/60">&middot;</span> Psychological Fiction <span className="text-dh-gold/60">&middot;</span> Romance
                </p>
              </Reveal>
            </div>
          </section>

          {/* ARC applications — opens Sept 19, 2026 at 10:00 AM PT */}
          {(() => { const arcOpen = Date.now() >= new Date(arcOpenAt).getTime(); return (
          <section data-testid="book-arc" className="border-y border-dh-cream/5 bg-dh-bg2 py-24 sm:py-28">
            <div className="mx-auto max-w-[780px] px-5 sm:px-8 text-center">
              <Reveal>
                <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">The Dhamma ARC Team</p>
              </Reveal>
              <Reveal delay={0.1}>
                {arcOpen ? (
                  <p className="mt-8 font-serif-soft text-2xl leading-relaxed text-dh-cream sm:text-3xl" data-testid="book-arc-status">
                    Advance Reader Copy applications for <span className="italic text-dh-gold">Lost Girl: The Beginning</span> are now open.
                    <span className="mt-4 block font-body text-base not-italic leading-relaxed text-dh-cream/70 sm:text-lg">
                      Selected readers will receive an advance copy before publication and will be invited to read and share an honest review.
                    </span>
                  </p>
                ) : (
                  <p className="mt-8 font-serif-soft text-2xl leading-relaxed text-dh-cream sm:text-3xl" data-testid="book-arc-status">
                    ARC applications open September 19 at 10:00 AM PT
                  </p>
                )}
              </Reveal>
              <Reveal delay={0.25}>
                <div className="mt-10 flex justify-center">
                  {arcOpen ? (
                    <CtaButton to="/arc" variant="gold" testId="book-arc-cta">
                      Apply for an ARC
                      <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
                    </CtaButton>
                  ) : (
                    <button type="button" disabled data-testid="book-arc-cta" className="inline-flex cursor-not-allowed items-center justify-center gap-3 border border-dh-gold/40 bg-transparent px-10 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-dh-gold/50 opacity-60">
                      Apply for an ARC
                    </button>
                  )}
                </div>
              </Reveal>
            </div>
          </section>
          ); })()}

          {/* Release information */}
          <section data-testid="book-release-info" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[780px] px-5 sm:px-8">
              <Reveal>
                <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Release Information</p>
                <div className="gold-rule mt-6 w-16" />
              </Reveal>
              <div className="mt-10 space-y-5">
                {[
                  ["Publication Date", firstBook.releaseDate],
                  ["Presale Begins", firstBook.presaleDate],
                  ["Series", "The Karter Legacy"],
                  ["Volume", "Book One"],
                  ["Author", "Bugg"],
                  ["Publisher", "Dhamma Publishing House"],
                ].map(([k, v], i) => (
                  <Reveal key={k} delay={0.08 + i * 0.06}>
                    <div className="grid grid-cols-3 gap-4 border-b border-dh-cream/5 pb-5">
                      <span className="font-body text-[11px] uppercase tracking-[0.18em] text-dh-muted">{k}</span>
                      <span className="col-span-2 font-serif-soft text-xl text-dh-cream">{v}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* About Bugg */}
          <section data-testid="book-about-bugg" className="border-y border-dh-cream/5 bg-dh-bg2 py-24 sm:py-32">
            <div className="mx-auto max-w-[780px] px-5 sm:px-8 text-center">
              <Reveal>
                <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">About Bugg</p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mx-auto mt-8 max-w-2xl font-body text-base leading-relaxed text-dh-cream/75 sm:text-lg">
                  {aboutBugg.sections[0].paragraphs[0]} {aboutBugg.sections[0].paragraphs[1]}
                </p>
                <p className="mx-auto mt-6 max-w-2xl font-serif-soft text-xl italic leading-relaxed text-dh-gold sm:text-2xl">
                  {aboutBugg.closing}
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <div className="mt-10 flex justify-center">
                  <CtaButton to="/about-bugg" variant="outline" testId="book-about-bugg-cta">
                    Meet Bugg
                    <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
                  </CtaButton>
                </div>
              </Reveal>
            </div>
          </section>

          {/* The Karter Legacy */}
          <section data-testid="book-series" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[780px] px-5 sm:px-8 text-center">
              <Reveal>
                <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">The Karter Legacy</p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-8 font-display text-3xl leading-snug text-dh-cream sm:text-4xl">
                  Lost Girl: The Beginning — Book One
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="mt-6 font-serif-soft text-lg italic text-dh-cream/60 sm:text-xl">
                  The story begins here.
                </p>
              </Reveal>
            </div>
          </section>

          <section className="pb-28 text-center">
            <Reveal>
              <div className="gold-rule mx-auto w-24" />
              <p className="mt-10 font-display text-2xl italic text-dh-gold sm:text-3xl">Stories That Leave Something Behind.</p>
            </Reveal>
          </section>
        </>
      )}

      {/* Spoiler-safe teaser — always present pre-reveal */}
      {stage < 3 && (
        <section data-testid="books-teaser" className="bg-dh-bg2 py-28 sm:py-36">
          <div className="mx-auto max-w-[850px] px-5 sm:px-8 text-center">
            <div className="space-y-3">
              {teaser.lines.map((line, i) => (
                <Reveal key={i} delay={i * 0.12}>
                  <p className="font-serif-soft text-2xl leading-relaxed text-dh-cream/85 sm:text-3xl">{line}</p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.5}>
              <p className="mt-12 font-display text-3xl italic text-dh-gold sm:text-4xl">{teaser.seal}</p>
            </Reveal>
            <Reveal delay={0.65}>
              <div className="mt-14 flex justify-center">
                <CtaButton to={books.cta.to} variant="gold" testId="books-cta">
                  {books.cta.label}
                  <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
                </CtaButton>
              </div>
            </Reveal>
          </div>
        </section>
      )}
    </>
  );
}
